import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const STORY = {
  en: {
    eyebrow: "The dreamcatcher that came before The Things We Keep",
    title: "A Portrait Made from Memory",
    imageCaption: "The dreamcatcher, with details from its weave.",
    cta: "Create together",
    imageAlt: "The dreamcatcher woven in natural cord with bark, feathers, shells and stones, with close-up details",
    blocks: [
      { t: "p", x: "Before The Things We Keep had a name, I made a dreamcatcher for Christian.\nIt was his birthday gift, but instead of buying him something he wanted, I began collecting small fragments of the life I had already shared with him." },
      { t: "p", x: "Stones. Beads. Feathers. Pieces of jewellery. Things that would mean very little to anyone else, but carried entire moments for us." },
      { t: "p", x: "Every feather came from somewhere.\nSome were from ducks Christian had hunted. One came from a magpie he shot after it had taken a baby bird from a nest. A single white feather came from one of our chickens." },
      { t: "p", x: "There were blue shells from Kragerø.\nA piece of bark, covered in lichen, that I picked up while walking in the forest with Christian and the children.\nA gold wing I had worn in my hair the very first time we saw each other." },
      { t: "p", x: "And a pale calcite stone, chosen for what it symbolised to me: clearing what no longer belonged and making space for something lighter." },
      { t: "p", x: "I added the initials of each of his children, held in place by lapis lazuli — a stone I associated with wisdom, truth, inner peace, clarity and honest communication.\nThere was pyrite, for strength and for breaking through what keeps us stuck.\nRock crystal, for balance and clarity.\nShungite, chosen for its symbolism of protection and restoration.\nAnd beads from an amazonite necklace I had worn to the very last party in the old main house at Nordhaug. To me, amazonite carried the idea of protection from negative energy, tension and pain." },
      { t: "p", x: "Even the love pendant had a history. It had once been my favourite piece of jewellery. I placed it there to amplify something I already experienced as powerful between us — love." },
      { t: "p", x: "And then there was one part Christian didn't know about.\nHis daughter had originally been meant to help me make the dreamcatcher. Life got in the way and we never managed it, so for a while the whole thing became our little secret.\nBecause months earlier, Christian had been lying in my bed looking at the dreamcatcher hanging above me.\nI remember almost bursting with happiness.\nHe didn't know then that his own was already beginning to exist." },
      { t: "img" },
      { t: "p", x: "Looking at it now, I realise this was one of the first times I made something in the language that would later become The Things We Keep.\nI wasn't choosing materials because they matched.\nI was choosing memories." },
      { t: "p", x: "A feather could hold a day.\nA stone could hold an intention.\nA piece of bark could hold a walk through the forest.\nA tiny gold wing could hold the first moment two people saw each other." },
      { t: "pull", x: "Individually, they were just things.\nTogether, they became a portrait." },
      { t: "p", x: "Not of what Christian looked like, but of how I had experienced him — the father, the hunter, the man I was falling in love with, and the life that was beginning to weave itself around us.\nPerhaps that is what I was trying to make all along:\nnot a dreamcatcher, but a way of making memory tangible." },
      { t: "p", x: "A story that could hang on a wall.\nA collection of seemingly ordinary fragments that, to the people who know their meaning, are anything but ordinary." },
      { t: "pull", x: "It could never be recreated, because the objects are not the artwork.\nThe story between them is." },
    ],
  },
  no: {
    eyebrow: "Drømmefangeren som kom før The Things We Keep",
    title: "Et portrett laget av minner",
    imageCaption: "Drømmefangeren, med detaljer fra fletningen.",
    cta: "Start en bestilling",
    imageAlt: "Drømmefangeren flettet i naturlig snor med bark, fjær, skjell og steiner, med detaljer",
    blocks: [
      { t: "p", x: "Før The Things We Keep hadde et navn, laget jeg en drømmefanger til Christian.\nDet var bursdagsgaven hans, men i stedet for å kjøpe noe han ønsket seg, begynte jeg å samle små fragmenter av livet jeg allerede hadde delt med ham." },
      { t: "p", x: "Steiner. Perler. Fjær. Smykker. Ting som ville bety svært lite for noen andre, men som bar hele øyeblikk for oss." },
      { t: "p", x: "Hver fjær kom fra et sted.\nNoen var fra ender Christian hadde jaktet på. En kom fra en skjære han skjøt etter at den hadde tatt en unge fra et rede. Én hvit fjær kom fra hønsene våre." },
      { t: "p", x: "Det var blå skjell fra Kragerø.\nEt barkstykke, dekket av lav, som jeg plukket opp da jeg gikk i skogen med Christian og barna.\nEn gullvinge jeg hadde hatt i håret da vi så hverandre for første gang." },
      { t: "p", x: "Og en blek kalkstein, valgt for det den symboliserte for meg: å klargjøre det som ikke lenger hørte hjemme, og gjøre plass til noe lettere." },
      { t: "p", x: "Jeg la til initialene til hvert av barna hans, holdt på plass av lapis lazuli — en stein jeg forbundet med visdom, sannhet, indre ro, klarhet og ærlig kommunikasjon.\nDet var pyritt, for styrke og for å bryte gjennom det som holder oss fast.\nBergkrystall, for balanse og klarhet.\nSjungitt, valgt for sin symbolikk om beskyttelse og gjenoppbygging.\nOg perler fra et amazonitt-smykke jeg hadde brukt til den siste festen i det gamle hovedhuset på Nordhaug. For meg bar amazonitt på ideen om beskyttelse mot negativ energi, spenning og smerte." },
      { t: "p", x: "Selv kjærlighetshengen hadde en historie. Den hadde en gang vært favorittsmykket mitt. Jeg plasserte den der for å forsterke noe jeg allerede opplevde som mektig mellom oss — kjærlighet." },
      { t: "p", x: "Og så var det én del Christian ikke visste om.\nDatteren hans skulle egentlig hjelpe meg med å lage drømmefangeren. Livet kom i veien, og vi rakk det aldri, så en stund var hele tingens vår lille hemmelighet.\nFor måneder tidligere hadde Christian ligget i sengen min og sett på drømmefangeren som hang over meg.\nJeg husker at jeg nesten sprak av glede.\nHan visste ikke da at hans egen allerede var på vei til å oppstå." },
      { t: "img" },
      { t: "p", x: "Når jeg ser på det nå, forstår jeg at dette var en av de første gangene jeg lagde noe i språket som senere ble The Things We Keep.\nJeg valgte ikke materialer fordi de passet sammen.\nJeg valgte minner." },
      { t: "p", x: "En fjær kunne bære en dag.\nEn stein kunne bære en hensikt.\nEt barkstykke kunne bære en tur gjennom skogen.\nEn liten gullvinge kunne bære det første øyeblikket to mennesker så hverandre." },
      { t: "pull", x: "Hver for seg var de bare ting.\nSammen ble de et portrett." },
      { t: "p", x: "Ikke av hvordan Christian så ut, men av hvordan jeg hadde opplevd ham — faren, jegeren, mannen jeg forelsket meg i, og livet som begynte å veve seg rundt oss.\nKanskje var det dette jeg hadde forsøkt å lage hele tiden:\nikke en drømmefanger, men en måte å gjøre minner taubare på." },
      { t: "p", x: "En historie som kunne henge på en vegg.\nEn samling av tilsynelatende hverdagslige fragmenter som, for dem som kjenner betydningen deres, er alt annet enn hverdagslige." },
      { t: "pull", x: "Det kunne aldri gjenskapes, for gjenstandene er ikke verket.\nHistorien mellom dem er det." },
    ],
  },
};

export default function DreamcatcherPage() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const t = STORY[lang];

  useEffect(() => {
    document.title = "A Portrait Made from Memory — The Things We Keep";
    return () => {
      document.title =
        lang === "no"
          ? "The Things We Keep — Gjenstander med en historie"
          : "The Things We Keep — Objects with a story";
    };
  }, [lang]);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="dreamcatcher-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="ttwk-label max-w-md" data-testid="dreamcatcher-eyebrow">
              {t.eyebrow}
            </p>
            <h1 className="mt-6 max-w-xl font-serif text-5xl font-light leading-[1.05] tracking-tight sm:text-7xl" data-testid="dreamcatcher-title">
              {t.title}
            </h1>
          </Reveal>
        </div>

        <div className="mt-16 max-w-2xl">
          {t.blocks.map((b, i) => {
            if (b.t === "img") {
              return (
                <Reveal key={i}>
                  <div className="img-frame my-16 overflow-hidden rounded-sm border border-clay/60">
                    <img
                      src="/media/dreamcatcher.jpg"
                      alt={t.imageAlt}
                      className="w-full object-cover"
                      data-testid="dreamcatcher-image"
                    />
                  </div>
                  <p className="-mt-10 font-serif text-sm italic text-umber/60">{t.imageCaption}</p>
                </Reveal>
              );
            }
            if (b.t === "pull") {
              return (
                <Reveal key={i}>
                  <p className="my-14 text-center font-serif text-3xl font-light italic leading-snug text-terracotta sm:text-4xl">
                    {b.x.split("\n").map((line, j) => (
                      <span key={j}>
                        {j > 0 && <br />}
                        {line}
                      </span>
                    ))}
                  </p>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.15)}>
                <p className="mt-8 font-serif text-xl font-light leading-relaxed text-umber first:mt-0 sm:text-2xl">
                  {b.x.split("\n").map((line, j) => (
                    <span key={j}>
                      {j > 0 && <br />}
                      {line}
                    </span>
                  ))}
                </p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="dreamcatcher-cta"
          >
            {t.cta}
          </button>
        </Reveal>
      </div>
    </div>
  );
}